import type { Branded } from '@moment/utility-types'

import { z } from 'zod'

const CUSIP_PATTERN = /^[0-9A-Z*@#]{8}[0-9]$/
const SPECIAL_CHAR_VALUES: Readonly<Record<string, number>> = {
  '*': 36,
  '@': 37,
  '#': 38,
}

function cusipCharValue(char: string): number {
  if (char >= '0' && char <= '9') {
    return Number(char)
  }
  if (char >= 'A' && char <= 'Z') {
    return char.charCodeAt(0) - 55
  }
  return SPECIAL_CHAR_VALUES[char] ?? Number.NaN
}

/** Modulus-10 "double-add-double" over the first eight characters. */
function hasValidCheckDigit(value: string): boolean {
  let sum = 0
  for (let index = 0; index < 8; index++) {
    const charValue = cusipCharValue(value.charAt(index))
    const weighted = index % 2 === 1 ? charValue * 2 : charValue
    sum += Math.floor(weighted / 10) + (weighted % 10)
  }
  return (10 - (sum % 10)) % 10 === Number(value.charAt(8))
}

/** A 9-character uppercase CUSIP whose last digit is its check digit. */
export type CusipStr = Branded<string, 'CusipStr'>

export const CusipStr = z
  .string()
  .regex(CUSIP_PATTERN)
  .refine(hasValidCheckDigit, { message: 'Invalid CUSIP check digit' })
  .transform((v): CusipStr => v as CusipStr)
